// Settings. Sectioned single-column page. Profile / Practice focus /
// Connections / Data & privacy.
//
// Coaching style is intentionally absent here. The comprehensive coaching
// system being built backend-side replaces user-selected styles.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import {
  getConnectorStatus,
  getMe,
  SessionExpiredError,
  updateMe,
  type ConnectorsStatusResponse,
  type Me,
} from "../lib/api";
import { signOut } from "../lib/auth";
import { Crumbs } from "../components/Crumbs";
import { PageTitle } from "../components/PageTitle";

const FOCUS_PLACEHOLDERS = [
  "I jump to a solution before asking a follow-up question",
  "I bury the recommendation under context",
  "I dominate team discussions when I should be widening input",
];

const MAX_FOCUS_ITEMS = 3;
const MAX_LABEL = 140;
const MIN_LABEL = 6;

export function Settings() {
  const [me, setMe] = useState<Me | null>(null);
  const [focusInputs, setFocusInputs] = useState<string[]>(["", "", ""]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectors, setConnectors] = useState<ConnectorsStatusResponse | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((m) => {
        if (cancelled) return;
        setMe(m);
        setFocusInputs(padFocus(m.practiceFocus.map((f) => f.label)));
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof SessionExpiredError) {
          navigate("/login", { replace: true });
          return;
        }
        setError(err instanceof Error ? err.message : "Could not load settings");
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    getConnectorStatus()
      .then((s) => {
        if (cancelled) return;
        setConnectors(s);
      })
      .catch(() => {
        // Non-blocking. The Connections section still renders with default
        // "not connected" state if the call fails.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function setFocusAt(idx: number, value: string) {
    setFocusInputs((prev) => prev.map((v, i) => (i === idx ? value : v)));
  }

  async function onSave() {
    setError(null);
    setSavedAt(null);
    const cleanedFocus = focusInputs.map((s) => s.trim()).filter(Boolean);
    for (const label of cleanedFocus) {
      if (label.length < MIN_LABEL) {
        setError(`Each focus needs at least ${MIN_LABEL} characters.`);
        return;
      }
      if (label.length > MAX_LABEL) {
        setError(`Each focus must be ${MAX_LABEL} characters or fewer.`);
        return;
      }
    }
    setSaving(true);
    try {
      // The Worker now accepts {id?, label}[] and preserves ids when a
      // label hasn't changed. We send labels only here; future polish can
      // track existing ids alongside each editable input.
      const updated = await updateMe({ practiceFocus: cleanedFocus.map((label) => ({ label })) });
      setMe(updated);
      setFocusInputs(padFocus(updated.practiceFocus.map((f) => f.label)));
      setSavedAt(Date.now());
    } catch (err) {
      // Don't redirect on session blip during save -- it would eat the user's
      // typed labels. Surface the error so they can sign in in another tab
      // and click save again.
      if (err instanceof SessionExpiredError) {
        setError("Your session expired. Open Sign in in a new tab, then click save again.");
        return;
      }
      setError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  }

  async function onSignOut() {
    await signOut();
    navigate("/", { replace: true });
  }

  if (!me && !error) {
    return (
      <>
        <Crumbs trail={["Settings"]} />
        <PageTitle>Settings.</PageTitle>
        <p style={{ color: "rgb(var(--color-ink-muted))" }}>Loading...</p>
      </>
    );
  }

  const activeFocusCount = focusInputs.filter((s) => s.trim().length >= MIN_LABEL).length;

  return (
    <>
      <Crumbs trail={["Settings"]} />
      <PageTitle lede="Tune what you're working on and which notetakers feed Klemento. Saves apply to every reading from now on.">
        Settings.
      </PageTitle>

      {error ? (
        <div
          role="alert"
          style={{
            padding: 14,
            borderRadius: 10,
            background: "rgb(var(--color-warning-soft))",
            color: "rgb(var(--color-warning))",
            fontSize: 14,
            marginBottom: 24,
          }}
        >
          {error}
        </div>
      ) : null}

      {/* Profile */}
      <section className="panel" style={{ marginBottom: 24 }} id="profile">
        <h3>Profile</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          <ProfileRow label="Name" value={me?.name || "(not set)"} />
          <ProfileRow label="Email" value={me?.email ?? ""} />
          <ProfileRow label="Plan" value={(me?.plan ?? "free").toUpperCase()} />
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => void onSignOut()}>
          Sign out
        </button>
      </section>

      {/* Practice focus */}
      <section className="panel" style={{ marginBottom: 24 }} id="practice-focus">
        <h3>Practice focus</h3>
        <p style={{ margin: "0 0 16px", color: "rgb(var(--color-ink-soft))", fontSize: 14, lineHeight: 1.55 }}>
          Pick one or two behaviours you're working on. Klemento checks every reading against them and tells you when one shows up. Phrase as "I" statements.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {focusInputs.map((value, idx) => (
            <label key={idx} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span className="frame-label">Focus {idx + 1}</span>
              <input
                type="text"
                className="input"
                value={value}
                onChange={(e) => setFocusAt(idx, e.target.value)}
                placeholder={FOCUS_PLACEHOLDERS[idx]}
                maxLength={MAX_LABEL}
              />
              <small style={{ color: "rgb(var(--color-ink-muted))", fontSize: 12 }}>
                {value.length}/{MAX_LABEL}
              </small>
            </label>
          ))}
        </div>
        <p style={{ margin: "16px 0 0", fontSize: 13, color: "rgb(var(--color-ink-muted))" }}>
          {activeFocusCount === 0
            ? "Leaving all three blank is fine. Klemento will fall back to general coaching."
            : `${activeFocusCount} active focus ${activeFocusCount === 1 ? "item" : "items"}.`}
        </p>
      </section>

      {/* Save action */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 32 }}>
        <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
          {saving ? "Saving..." : "Save practice focus"}
        </button>
        {savedAt ? (
          <span style={{ fontSize: 13, color: "rgb(var(--color-sage-deep))" }}>Saved.</span>
        ) : null}
      </div>

      {/* Connections */}
      <section className="panel" style={{ marginBottom: 24 }} id="connections">
        <h3>Connections</h3>
        <p style={{ margin: "0 0 16px", color: "rgb(var(--color-ink-soft))", fontSize: 14, lineHeight: 1.55 }}>
          Connect a notetaker once and Klemento will coach you on every meeting it records.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <ConnectorCard
            provider="granola"
            name="Granola"
            blurb="AI notepad for meetings."
            status={connectors?.granola}
          />
          <ConnectorCard
            provider="fathom"
            name="Fathom"
            blurb="AI meeting recorder, free tier available."
            status={connectors?.fathom}
          />
        </div>
      </section>

      {/* Data & privacy */}
      <section className="panel" id="data">
        <h3>Data & privacy</h3>
        <p style={{ margin: 0, color: "rgb(var(--color-ink-soft))", fontSize: 14, lineHeight: 1.55 }}>
          Klemento never stores raw transcripts. Each reading persists only the structured analysis (scores, evidence quotes, focus checks). Export and delete tooling is on the way.
        </p>
      </section>
    </>
  );
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", gap: 16, alignItems: "baseline" }}>
      <span
        className="frame-label"
        style={{ width: 60, flexShrink: 0, margin: 0 }}
      >
        {label}
      </span>
      <span style={{ fontSize: 14, color: "rgb(var(--color-ink))" }}>{value}</span>
    </div>
  );
}

function ConnectorCard({
  provider,
  name,
  blurb,
  status,
}: {
  provider: "granola" | "fathom";
  name: string;
  blurb: string;
  status?: { connected: boolean; expired?: boolean };
}) {
  const connected = status?.connected ?? false;
  const expired = Boolean(status?.expired);
  const liveConnection = connected && !expired;

  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        alignItems: "center",
        justifyContent: "space-between",
        padding: 14,
        borderRadius: 10,
        background: "rgb(var(--color-bg))",
        border: "1px solid rgb(var(--color-border))",
        flexWrap: "wrap",
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{name}</span>
          {liveConnection ? (
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                padding: "2px 8px",
                borderRadius: 999,
                background: "rgb(var(--color-sage-deep) / 0.18)",
                color: "rgb(var(--color-sage-deep))",
              }}
            >
              Connected
            </span>
          ) : expired ? (
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                padding: "2px 8px",
                borderRadius: 999,
                background: "rgb(var(--color-warning-soft))",
                color: "rgb(var(--color-warning))",
              }}
            >
              Re-connect
            </span>
          ) : null}
        </div>
        <div style={{ fontSize: 13, color: "rgb(var(--color-ink-muted))" }}>{blurb}</div>
      </div>
      <Link to={`/connectors/${provider}`} className={liveConnection ? "btn btn-secondary" : "btn btn-primary"}>
        {liveConnection ? "Manage" : expired ? "Re-connect" : "Connect"}
      </Link>
    </div>
  );
}

function padFocus(labels: string[]): string[] {
  const out = labels.slice(0, MAX_FOCUS_ITEMS);
  while (out.length < MAX_FOCUS_ITEMS) out.push("");
  return out;
}
